import React, { useRef, useEffect, useContext, useState } from "react";
import { BoardNote } from "@/bryntum-lib/models/BoardNote";
import { EntityRecord } from "@/types/entity";
import { useSwivuRichEditor } from "@/react/hooks/useSwivuRichEditor";
import { useSwivuNotes } from "@/react/hooks/entityHooks/useSwivuNotes";
import { Button } from "@/modern/components/ui/button";
import { Label } from "@/modern/components/ui/label";
import { Textarea } from "@/modern/components/ui/textarea";
import { Controller, useForm } from "react-hook-form";
import Combobox from "@/modern/components/ui/combobox";
import { DateHelper } from "@bryntum/schedulerpro";
import BryntumContext from "@/react/context/bryntumContext";
import { AlertTriangle, Loader2, X } from "lucide-react";
import { checkGrantOrder, cn } from "@/modern/utils";
import { Dialog, DialogFooter } from "@/modern/components/ui/dialog-shadcn";
import { SharedModalBody, SharedModalDescription, SharedModal, SharedModalHeader, SharedModalTitle } from "@/modern/components/shared-modal-component";
import { NotesEditorHeader } from "./NotesEditorHeader";
import { NotesEditorProvider } from "@/react/components/entityViews/notes/context/NotesEditorContext";

interface NotesEditorProps {
  noteRecord: BoardNote | null;
  entityRecord: EntityRecord;
  onFinish?: () => void;
  isDirty?: (isDirty: boolean) => void;
  showUnsavedDialog?: boolean;
  setShowUnsavedDialog?: (showUnsavedDialog: boolean) => void;
  onDeleteNote?: () => void;
}

// Reusable metadata card component
const MetadataCard = ({ label, value }: { label: string; value: string | React.ReactNode }) => (
  <div className="rounded-lg border border-border/70 bg-muted/20 px-4 py-3">
    <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
    <p className="mt-1 font-medium text-foreground">{value || "N/A"}</p>
  </div>
);

export const NotesEditor = ({ noteRecord, entityRecord, onFinish, isDirty, showUnsavedDialog, setShowUnsavedDialog, onDeleteNote }: NotesEditorProps) => {
  const richTextRef = useRef<HTMLDivElement>(null);
  const { swivuAppGrants, selectedBoard } = useContext(BryntumContext);
  const { saveNote, loading, getNoteData, updateNote, canEditRecord } = useSwivuNotes({ entityRecord });
  const { initialize, hasChanges, markAsSaved, noteData, wordCount, isInitialized, setNoteData } = useSwivuRichEditor();
  const isNewNote = noteRecord?.isPhantom;
  const canEdit = isNewNote ? true : noteRecord ? canEditRecord(noteRecord) : false;
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  const form = useForm({
    defaultValues: {
      description: noteRecord?.description,
      archived: noteRecord?.archived,
      canEdit: noteRecord?.canEdit,
      canView: noteRecord?.canView
    }
  });

  // Computed values for cleaner code
  const isFieldsDisabled = isNewNote || !canEdit || !noteRecord;
  const isSaveDisabled = (!hasChanges && !form.formState.isDirty) || !canEdit || !noteRecord;

  // Centralized save logic to avoid duplication
  const saveChanges = async (formData?: Partial<BoardNote>) => {
    if (!noteRecord) return;

    if (hasChanges) {
      await saveNote(noteRecord, {
        content: noteData,
        board: isNewNote ? Number(selectedBoard?.id) : noteRecord.boardId,
        archived: isNewNote ? false : noteRecord.archived
      });
      markAsSaved();
    }

    if (form.formState.isDirty) {
      const data = formData || form.getValues();
      updateNote(noteRecord, {
        canEdit: data.canEdit,
        canView: data.canView,
        description: data.description
      });
    }
  };

  const onSubmit = async (data: Partial<BoardNote>) => {
    await saveChanges(data);
  };

  const handleClose = () => {
    // Check if there are unsaved changes
    if (hasChanges || form.formState.isDirty) {
      setShowUnsavedDialog?.(true);
    } else {
      onFinish?.();
    }
  };

  // Handle dialog actions
  const handleKeepEditing = () => {
    setShowUnsavedDialog?.(false);
  };

  const handleDontSave = () => {
    setShowUnsavedDialog?.(false);
    onFinish?.();
  };

  const handleSaveAndClose = async () => {
    await saveChanges();
    setShowUnsavedDialog?.(false);
    onFinish?.();
  };

  useEffect(() => {
    isDirty?.(hasChanges || form.formState.isDirty);
  }, [hasChanges, form.formState.isDirty]);

  useEffect(() => {
    if (!noteRecord) return;
    if (richTextRef.current && !isInitialized) {
      initialize(richTextRef.current as HTMLElement);
      if (!isNewNote) {
        getNoteData(noteRecord.id).then((data) => {
          setNoteData(data || "");
        });
      }
    }
  }, [initialize, isInitialized, setNoteData, getNoteData, noteRecord]);

  // Sync form values when noteRecord changes (especially when isPhantom changes and backend updates values)
  useEffect(() => {
    form.reset({
      description: noteRecord?.description,
      archived: noteRecord?.archived,
      canEdit: noteRecord?.canEdit,
      canView: noteRecord?.canView
    });
  }, [noteRecord?.description, noteRecord?.archived, noteRecord?.canEdit, noteRecord?.canView, form]);

  if (!noteRecord) return null;

  return (
    <NotesEditorProvider
      noteRecord={noteRecord}
      entityRecord={entityRecord}
      isNewNote={!!isNewNote}
      isSaveDisabled={isSaveDisabled}
      isSettingsOpen={isSettingsOpen}
      wordCount={wordCount}
      onSettingsToggle={() => setIsSettingsOpen(!isSettingsOpen)}
      onDeleteNote={onDeleteNote}
      onClose={handleClose}
    >
      <div className="flex flex-col min-h-0 flex-1">
        <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col flex-1 min-h-0">
          <NotesEditorHeader className="pl-6 pr-10 py-4" />

          {/* Main Content Area with Embedded Sidebar */}
          <div className="flex-1 flex min-h-0 relative py-6 px-6 ">
            {/* Rich Text Editor */}
            <div className={cn("flex-1 relative transition-all duration-300 overflow-y-auto min-h-[50vh] border rounded-md", isSettingsOpen && "mr-0")}>
              {loading && (
                <div className="absolute z-50 top-0 left-0 w-full h-full flex items-center justify-center bg-background/50 backdrop-blur-sm">
                  <Loader2 className="h-4 w-4 animate-spin" />
                </div>
              )}
              <div className="h-full  w-full [&_.ck-toolbar]: bg-background ">
                <div ref={richTextRef} />
              </div>
            </div>

            {/* Embedded Settings Sidebar */}
            <div className={cn(" bg-background overflow-y-auto transition-all duration-300 ease-out", isSettingsOpen ? "w-80 opacity-100" : "w-0 opacity-70")}>
              {isSettingsOpen && (
                <>
                  {/* Header */}
                  <div className="flex py-1.5 px-4 items-center justify-between sticky top-0 bg-background z-10 border-b">
                    <h3 className="text-base">Settings</h3>
                    <div className="flex items-center gap-2">
                      <Button type="button" variant="ghost" size="icon-sm" onClick={() => setIsSettingsOpen(false)}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="grid gap-y-10 pb-4">
                    {/* Permissions Section */}
                    <div className="space-y-3 px-4 pt-6">
                      <h4 className="text-xs text-muted-foreground uppercase">Permissions</h4>
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="canEdit" className="text-sm font-medium">
                            Can Edit
                          </Label>
                          <Controller
                            name="canEdit"
                            control={form.control}
                            disabled={isFieldsDisabled}
                            render={({ field }) => (
                              <Combobox
                                size="sm"
                                {...field}
                                options={swivuAppGrants}
                                value={field.value}
                                onValueChange={field.onChange}
                                placeholder="Select who can edit"
                                clearable={false}
                                disabled={isFieldsDisabled}
                                onBeforeSelect={({ value }) => {
                                  return checkGrantOrder({ canEdit: Number(value || 0), canView: form.getValues("canView") || 0, form });
                                }}
                              />
                            )}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="canView" className="text-sm font-medium">
                            Can View
                          </Label>
                          <Controller
                            name="canView"
                            control={form.control}
                            disabled={isFieldsDisabled}
                            render={({ field }) => (
                              <Combobox
                                {...field}
                                options={swivuAppGrants}
                                value={field.value}
                                onValueChange={field.onChange}
                                placeholder="Select who can view"
                                clearable={false}
                                disabled={isFieldsDisabled}
                                onBeforeSelect={({ value }) => {
                                  return checkGrantOrder({ canEdit: form.getValues("canEdit") || 0, canView: Number(value || 0) });
                                }}
                              />
                            )}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Description Section */}
                    <div className="space-y-2 px-4">
                      <Label htmlFor="description" className="text-xs text-muted-foreground uppercase">
                        Description
                      </Label>
                      <Controller
                        name="description"
                        disabled={isFieldsDisabled}
                        control={form.control}
                        render={({ field }) => <Textarea {...field} placeholder="Add a description for this note" className="min-h-[100px] resize-none" disabled={isFieldsDisabled} />}
                      />
                    </div>

                    {/* Metadata Section */}
                    <div className="space-y-3 px-4">
                      <h4 className="text-xs text-muted-foreground uppercase">Metadata</h4>
                      <div className="grid gap-3 text-sm">
                        <MetadataCard
                          label="Last Updated"
                          value={
                            <>
                              {noteRecord?.lastUpdatedBy}
                              <br />
                              {noteRecord?.lastUpdatedAt && DateHelper.format(new Date(noteRecord.lastUpdatedAt), "MM/DD/YYYY")}
                            </>
                          }
                        />
                        <MetadataCard label="Size" value={noteRecord?.formatFileSize} />
                        <MetadataCard label="Added By" value={noteRecord?.creatorName} />
                        <MetadataCard label="Age" value={String(noteRecord?.aging)} />
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </form>

        {/* Unsaved Changes Confirmation Dialog */}
        <Dialog open={showUnsavedDialog} onOpenChange={setShowUnsavedDialog}>
          <SharedModal className="md:max-w-md flex-col top-1/2 h-auto">
            <SharedModalHeader>
              <SharedModalTitle className="flex items-center gap-2 text-lg font-semibold">
                <AlertTriangle className="size-6 mr-2 text-yellow-500" /> Unsaved Changes
              </SharedModalTitle>
              <SharedModalDescription>You have unsaved changes. What would you like to do?</SharedModalDescription>
            </SharedModalHeader>
            <SharedModalBody>
              <DialogFooter className="flex-col sm:flex-row gap-3 border-t-0">
                <Button variant="ghost" onClick={handleKeepEditing} size="sm">
                  Keep working
                </Button>
                <Button variant="destructive" onClick={handleDontSave} size="sm">
                  Do not save
                </Button>
                <Button variant="success" onClick={handleSaveAndClose} size="sm">
                  Save & close
                </Button>
              </DialogFooter>
            </SharedModalBody>
          </SharedModal>
        </Dialog>
      </div>
    </NotesEditorProvider>
  );
};
